import { computed, onMounted, ref, watch } from 'vue'
import useGet from '@/core/composables/api/useGet'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import { useI18n } from 'vue-i18n'
import type {
  OrganizationFeedbackTopicViewModel,
  OrganizationFeedbackTopicsViewModel,
} from '../models/organization-feedback-topic'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'

export const useOrganizationFeedbackTopics = () => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const { data, load, status, reload } = useGet<OrganizationFeedbackTopicsViewModel>(
    'organization/feedback-topics',
  )

  const feedbackTopicStore = useFeedbackTopicStore()

  const feedbackTopics = ref<OrganizationFeedbackTopicViewModel[]>([])
  const feedbackTopicIdToUpdate = ref<string | null>(null)

  const endpoint = computed(() => {
    if (!feedbackTopicIdToUpdate.value) {
      return 'organization/feedback-topics'
    }
    return `organization/feedback-topics/${feedbackTopicIdToUpdate.value}`
  })

  const { mutate, status: updateStatus } = usePut<string, { enabled: boolean }>(endpoint)

  onMounted(() => {
    load()
  })

  watch(
    () => data.value,
    (newData) => {
      feedbackTopics.value = [...(newData?.feedbackTopics ?? [])]
    },
    { deep: true },
  )

  const setFeedbackTopicEnabled = async (feedbackTopicId: string, enabled: boolean) => {
    feedbackTopicIdToUpdate.value = feedbackTopicId

    await mutate({ enabled })

    if (updateStatus.value == ApiRequestStatus.Success) {
      feedbackTopics.value = feedbackTopics.value.map((topic) => {
        if (topic.id === feedbackTopicId) {
          return { ...topic, enabled }
        }
        return topic
      })
      snackStore.showInfo(
        enabled
          ? t('settings.feedbackTopics.snackbar.enableSuccess')
          : t('settings.feedbackTopics.snackbar.disableSuccess'),
      )
      await feedbackTopicStore.reloadTemplates()
    } else {
      snackStore.showError(
        enabled
          ? t('settings.feedbackTopics.snackbar.enableError')
          : t('settings.feedbackTopics.snackbar.disableError'),
      )
      await reload()
    }

    feedbackTopicIdToUpdate.value = null
  }

  return {
    status,
    feedbackTopics,
    setFeedbackTopicEnabled,
  }
}
